/**
 * NER-4488 fase 2/3 — Selector de plantillas reutilizable.
 *
 * Panel con cards (icono, nombre, descripción, chips). Lo usan la página de
 * Modificadores (grupos comunes), la de Promociones (reglas precargadas) y el
 * wizard de combos. Además del catálogo del sistema puede mostrar una sección
 * "Plantillas de mi organización" (fase 3) con borrado inline.
 *
 * Props:
 *   - title, hint          textos del encabezado
 *   - helpTopicKey         topic del registry para el <HelpIcon /> (opcional)
 *   - templates            [{ id, icon, name, description, chips = [], disabled = false }]
 *   - onSelect(id)         click en una card del catálogo
 *   - onClose()            si se pasa, muestra el botón "Ocultar plantillas"
 *   - ownTemplates         [{ id, name, description, chips, meta }] plantillas de la organización
 *   - ownTitle, ownHint    textos de la sección propia (default i18n)
 *   - onSelectOwn(id)      click en una plantilla propia
 *   - onDeleteOwn(id)      si se pasa, cada plantilla propia tiene "Eliminar" (con confirmación inline)
 *   - ownLoading           muestra "Cargando…" en la sección propia
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2, Building2 } from 'lucide-react';
import HelpIcon from './help/HelpIcon';
import './TemplatePicker.css';

function TemplateCard({ tpl, onSelect, onDelete, confirmDeleteLabel, deleteLabel, cancelLabel }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className={`template-card ${tpl.own ? 'template-card--own' : ''}`}>
      <button
        type="button"
        className="template-card__main"
        onClick={() => onSelect(tpl.id)}
        disabled={tpl.disabled}
      >
        {tpl.icon && <span className="template-card__icon">{tpl.icon}</span>}
        <span className="template-card__name">{tpl.name}</span>
        {tpl.description && <span className="template-card__desc">{tpl.description}</span>}
        {tpl.chips && tpl.chips.length > 0 && (
          <span className="template-card__chips">
            {tpl.chips.map((chip, index) => (
              <span key={`${tpl.id}-chip-${index}`} className="template-chip">
                {chip}
              </span>
            ))}
          </span>
        )}
        {tpl.meta && <span className="template-card__meta">{tpl.meta}</span>}
      </button>
      {onDelete && (
        <div className="template-card__actions">
          {confirming ? (
            <>
              <span className="template-card__confirm">{confirmDeleteLabel}</span>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => {
                  setConfirming(false);
                  onDelete(tpl.id);
                }}
              >
                {deleteLabel}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setConfirming(false)}
              >
                {cancelLabel}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setConfirming(true)}
              aria-label={`${deleteLabel}: ${tpl.name}`}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TemplatePicker({
  title,
  hint,
  helpTopicKey,
  templates = [],
  onSelect,
  onClose,
  ownTemplates,
  ownTitle,
  ownHint,
  onSelectOwn,
  onDeleteOwn,
  ownLoading = false,
}) {
  const { t } = useTranslation();
  const showOwn = Array.isArray(ownTemplates);

  return (
    <section className="template-picker" aria-label={title}>
      <div className="template-picker__head">
        <div>
          <h2 className="template-picker__title">
            {title}
            {helpTopicKey && <HelpIcon topicKey={helpTopicKey} />}
          </h2>
          {hint && <p className="template-picker__hint">{hint}</p>}
        </div>
        {onClose && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={14} aria-hidden="true" /> {t('templates.hideTemplates')}
          </button>
        )}
      </div>

      {showOwn && (
        <div className="template-picker__own">
          {templates.length > 0 && (
            <h3 className="template-picker__subtitle">
              <Building2 size={16} aria-hidden="true" />
              {ownTitle || t('templates.own.title')}
            </h3>
          )}
          {ownLoading ? (
            <p className="template-picker__hint">{t('common.loading')}</p>
          ) : ownTemplates.length === 0 ? (
            <p className="template-picker__hint">{ownHint || t('templates.own.empty')}</p>
          ) : (
            <div className="template-picker__grid">
              {ownTemplates.map((tpl) => (
                <TemplateCard
                  key={tpl.id}
                  tpl={{
                    ...tpl,
                    own: true,
                    icon: tpl.icon || <Building2 size={20} aria-hidden="true" />,
                  }}
                  onSelect={onSelectOwn || onSelect}
                  onDelete={onDeleteOwn}
                  confirmDeleteLabel={t('templates.own.confirmDelete')}
                  deleteLabel={t('common.delete')}
                  cancelLabel={t('common.cancel')}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showOwn && templates.length > 0 && (
        <h3 className="template-picker__subtitle">{t('templates.own.catalogTitle')}</h3>
      )}

      <div className="template-picker__grid">
        {templates.map((tpl) => (
          <TemplateCard key={tpl.id} tpl={tpl} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

export default TemplatePicker;
